import { callable } from '@/services/api/backend';
import type {
  EngagementInput, EngagementRow, MessageChannel, PartnerMessage, Partnership, PartnershipAutomations, PartnershipFilter, PartnershipInput,
  PartnershipStats, PartnershipStatus, PartnershipTrendPoint, TrendPeriodP, UrgentItem,
} from '@/types';

/**
 * Partnerships API. Straight to the endeeko callables — deliberately NO mock fallback:
 * a failure is an error the UI shows, never invented data.
 */
export interface PartnershipsPage { partnerships: Partnership[]; total: number; page: number; hasMore: boolean }
export interface ExportResult { data: string; filename: string; mime: string }

export const partnershipsApi = {
  stats: async (companyId: string): Promise<PartnershipStats> => {
    const r = await callable<PartnershipStats & { thisMonth?: number }>('getPartnershipStats', { companyId });
    return { total: r.total, active: r.active, pending: r.pending, month: r.month ?? r.thisMonth ?? 0 };
  },
  list: (companyId: string, o: { filter: PartnershipFilter; search: string; page: number; limit?: number; status?: PartnershipStatus }) =>
    callable<PartnershipsPage>('getPartnerships', { companyId, ...o, limit: o.limit ?? 20 }),
  get: (companyId: string, partnershipId: string) =>
    callable<{ partnership: Partnership; engagement: EngagementRow[]; messages: PartnerMessage[] }>('getPartnership', { companyId, partnershipId }),
  trend: async (companyId: string, period: TrendPeriodP): Promise<PartnershipTrendPoint[]> =>
    (await callable<{ trend: PartnershipTrendPoint[] }>('getPartnershipTrend', { companyId, period })).trend,
  urgent: async (companyId: string): Promise<UrgentItem[]> =>
    (await callable<{ urgent: UrgentItem[] }>('getUrgentPartnerships', { companyId })).urgent,
  resolveUrgent: (companyId: string, key: string, action: 'snooze' | 'dismiss' | 'resolve', days?: number) =>
    callable<{ success: boolean }>('resolvePartnershipAction', { companyId, key, action, days }),

  engagement: async (companyId: string, partnershipId?: string): Promise<EngagementRow[]> =>
    (await callable<{ engagement: EngagementRow[] }>('getPartnershipEngagement', { companyId, partnershipId, limit: 50 })).engagement,
  saveEngagement: (companyId: string, e: EngagementInput) => callable<{ id: string }>('savePartnershipEngagement', { companyId, ...e }),
  deleteEngagement: (companyId: string, id: string) => callable<{ success: boolean }>('deletePartnershipEngagement', { companyId, id }),

  messages: async (companyId: string, partnershipId?: string, limit = 20): Promise<PartnerMessage[]> =>
    (await callable<{ messages: PartnerMessage[] }>('getPartnershipMessages', { companyId, partnershipId, limit })).messages,
  sendMessage: (companyId: string, m: { partnershipId: string; type: MessageChannel; direction: 'outbound' | 'inbound'; subject?: string; content: string; productName?: string; senderName?: string }) =>
    callable<{ id: string; delivered: boolean; note?: string }>('sendPartnerMessage', { companyId, ...m }),
  markMessage: (companyId: string, id: string, action: 'read' | 'archive') =>
    callable<{ success: boolean }>('markPartnershipMessage', { companyId, id, action }),

  create: (companyId: string, p: PartnershipInput) => callable<{ id: string }>('createPartnership', { companyId, partnership: p }),
  update: (companyId: string, partnershipId: string, p: PartnershipInput) => callable<{ success: boolean }>('updatePartnership', { companyId, partnershipId, partnership: p }),
  setStatus: (companyId: string, partnershipId: string, status: PartnershipStatus) =>
    callable<{ success: boolean; status: PartnershipStatus }>('updatePartnershipStatus', { companyId, partnershipId, status }),
  remove: (companyId: string, partnershipId: string) => callable<{ success: boolean }>('deletePartnership', { companyId, partnershipId }),
  addNote: (companyId: string, partnershipId: string, note: string) =>
    callable<{ success: boolean; notes: string }>('addPartnershipNote', { companyId, partnershipId, note }),

  exportAll: (companyId: string, format: 'csv' | 'json', filters: { status?: PartnershipStatus; type?: string } = {}) =>
    callable<ExportResult>('exportPartnerships', { companyId, format, filters }),

  automations: async (companyId: string): Promise<PartnershipAutomations> =>
    (await callable<{ automations: PartnershipAutomations }>('getPartnershipAutomations', { companyId })).automations,
  updateAutomations: async (companyId: string, patch: Partial<{ [K in keyof PartnershipAutomations]: Partial<PartnershipAutomations[K]> }>): Promise<PartnershipAutomations> =>
    (await callable<{ automations: PartnershipAutomations }>('updatePartnershipAutomations', { companyId, patch })).automations,

  // AI agents (tokens debited server-side)
  summary: (companyId: string, partnershipId: string) =>
    callable<{ bullets: string[]; tokens: number; cost: number }>('summarizePartnership', { companyId, partnershipId }),
  outreach: (companyId: string, partnershipId: string, goal?: string, senderName?: string) =>
    callable<{ subject: string; body: string; tokens: number; cost: number }>('draftPartnerOutreach', { companyId, partnershipId, goal, senderName }),
  optimizeShare: (companyId: string, partnershipId: string) =>
    callable<{ analysis: string[]; tokens: number; cost: number }>('optimizeRevenueShare', { companyId, partnershipId }),
  contractRisk: (companyId: string, partnershipId: string, contractText: string) =>
    callable<{ analysis: string[]; tokens: number; cost: number }>('analyzeContractRisk', { companyId, partnershipId, contractText }),
  recommendPartners: (companyId: string, focus?: string) =>
    callable<{ suggestions: string[]; tokens: number; cost: number }>('recommendPartners', { companyId, focus }),
};

/** Save a backend-generated export to disk. */
export function downloadExport(r: ExportResult) {
  const url = URL.createObjectURL(new Blob([r.data], { type: r.mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = r.filename;
  a.click();
  URL.revokeObjectURL(url);
}
